import { useState } from "react";
import useCod from "../../../data/useCod";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import useSingleLead from "../../../data/useSingleLead";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { DateTime } from "luxon";
import useNotification from "../../notifications/useNotifications";
import LoadingWheel from "../../shared/LoadingWheel";
import { useQueryClient } from "@tanstack/react-query";
import { getLeadLocation } from "../../../utils/getLeadLocation";
import useCustomerDetails from "../../../data/useCustomerDetails";
import { IAddressWithCoordinates } from "../../../models/IAddressFields";


export default function CODStatus({ leadId, showAddress = false }: { leadId: string, showAddress?: boolean }) {
  const queryClient = useQueryClient();

  const { lead, downloadDoc } = useSingleLead(leadId, "CODStatus");
  const { customer } = useCustomerDetails();
  const { update: updateVehicle } = useVehiclesMutations();
  const { runCod } = useCod();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");

  const [confirmCodOpen, setConfirmCodOpen] = useState(false);

  // Resolve the address used for the COD. When the vehicle has no address at all,
  // placeholder-fill it with the business address (mirrors the API fallback in SubmitCod).
  const vehicleLocation = getLeadLocation(lead.data?.data);
  const vehicleAddressEmpty = [
    vehicleLocation?.line1,
    vehicleLocation?.line2,
    vehicleLocation?.line3,
    vehicleLocation?.line4,
    vehicleLocation?.locality,
    vehicleLocation?.town,
    vehicleLocation?.county,
    vehicleLocation?.postcode,
  ].every((field) => !field || !field.trim());

  const businessAddress = customer.data?.data;
  const usingBusinessAddress = vehicleAddressEmpty && !!businessAddress;

  const location: IAddressWithCoordinates | null = usingBusinessAddress
    ? {
        line1: businessAddress!.line1 ?? null,
        line2: businessAddress!.line2 ?? null,
        line3: businessAddress!.line3 ?? null,
        line4: businessAddress!.line4 ?? null,
        locality: businessAddress!.locality ?? null,
        town: businessAddress!.town ?? null,
        county: businessAddress!.county ?? null,
        postcode: businessAddress!.postcode ?? null,
      }
    : vehicleLocation;

  const handleCodRequired = () => {
    let updatedVehicle = lead.data?.data.vehicle!;

    updatedVehicle.codRequired = !updatedVehicle.codRequired;

    updateVehicle.mutate(updatedVehicle, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable"])
      },
    });
  };

  const handleCodConfirmed = (confirm: boolean) => {
    let updatedVehicle = lead.data?.data.vehicle!;
    const codConfirmedOn = confirm ? DateTime.now().toISO() : null;

    updatedVehicle.codConfirmedOn = codConfirmedOn;

    updateVehicle.mutate(updatedVehicle, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable"])
      },
    });
  };

  const handleGenerateCod = () => {
    if (!location?.line1 || !location?.postcode) {
      addNotification({
        variant: "error",
        primaryText: "Cannot generate COD certificate. Please ensure the vehicle address has at least Address Line 1 and Postcode."
      });
      return;
    }

    runCod.mutate(lead.data?.data.id, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setConfirmCodOpen(false);
        queryClient.invalidateQueries(["pagedTable"])
      },
      onError: (error: any) => {
        //saveHandlers.onError();
        addNotification({
          variant: "error",
          primaryText: error.message,
        });
      }
    });
  }

  const downloadCodCertificate = () => {
    var ids = {
      leadId: lead.data?.data.id,
      docId: lead.data?.data.vehicle?.codCertificateDocument
    };

    downloadDoc.mutate(ids, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.type = 'image/png';
        link.download = `${lead.data?.data.vehicle?.vrm} COD Certificate.png`;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  }

  if (lead.isLoading) {
    return <LoadingWheel />
  }

  return (
    <>
      <ConfirmModal open={confirmCodOpen} setOpen={setConfirmCodOpen} title="Confirm COD" confirmButtonText="Generate COD"
        message="Are you sure you want to create a certificate of destruction for this vehicle? This process is irreversible"
        onConfirm={handleGenerateCod} isLoading={runCod.isLoading}
      />

      <div className="flex flex-col gap-y-4 divide-y">

        {showAddress && (
          <div>
            {usingBusinessAddress && (
              <p className="text-xs italic text-gray-400 pb-2">
                No vehicle address on file — using the business address.
              </p>
            )}
          <div className="grid grid-cols-2 gap-x-8">
            {[
              { label: 'Line 1', value: location?.line1 },
              { label: 'Line 2', value: location?.line2 },
              { label: 'Line 3', value: location?.line3 },
              { label: 'Locality', value: location?.locality },
              { label: 'Town', value: location?.town },
              { label: 'County', value: location?.county },
              { label: 'Postcode', value: location?.postcode },
            ].map((field, index) => (
              <div key={index} className="grid grid-cols-2 gap-2 items-center py-2">
                <dt className="text-sm font-medium text-gray-500">
                  {field.label}
                </dt>
                <dd className="text-sm text-gray-900">
                  {field.value || <span className="text-gray-300 italic">Not provided</span>}
                </dd>
              </div>
            ))}
          </div>
          </div>
        )}

        <div className="divide-y">

          <div className="py-2 grid grid-cols-2 gap-4 items-center">
            <dt className="text-sm font-medium text-gray-500">
              COD Required
            </dt>
            <dd className="flex justify-between items-center text-sm text-gray-900">
              <div>
                {lead.data?.data.vehicle?.codRequired ? "Yes" : "No"}
              </div>
              {!lead.data?.data.vehicle?.codCertificateNumber && (
                <button
                  type="button"
                  onClick={handleCodRequired}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Toggle
                </button>
              )}
            </dd>
          </div>

          {(lead.data?.data.vehicle?.codRequired || lead.data?.data.vehicle?.codCertificateNumber) && (
            <>
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  COD Confirmed
                </dt>
                <dd className="flex justify-between items-center text-sm text-gray-900">
                  <div>
                    {lead.data?.data.vehicle?.codConfirmedOn ? "Yes" : "No"}
                  </div>

                  {!lead.data?.data.vehicle?.codCertificateNumber && (
                    lead.data?.data.vehicle?.codConfirmedOn ? (
                      <button
                        type="button"
                        onClick={() => handleCodConfirmed(false)}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Toggle
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleCodConfirmed(true)}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Toggle
                      </button>
                    )
                  )}
                </dd>
              </div>

              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  {lead.data?.data.vehicle?.codCertificateNumber ? "COD Certificate Number" : "Generate COD"}
                </dt>
                <dd className="flex justify-between items-center text-sm text-gray-900">
                  <span>{lead.data?.data.vehicle?.codCertificateNumber ?? ""}</span>
                  {!lead.data?.data.vehicle?.codCertificateNumber ? (
                    <button
                      type="button"
                      onClick={() => setConfirmCodOpen(true)}
                      className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                      Generate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => downloadCodCertificate()}
                      className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                      Download Certificate
                    </button>
                  )}
                </dd>
              </div>

            </>
          )}
        </div>

      </div>

    </>
  )
}